import type { UserQuestionAnswer } from '@/frontend/pages/Dashboard/AgentChats/types'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import { Head, router, usePage } from '@inertiajs/react'
import { MessagesSquare, MoreHorizontal, Pin, PinOff, Plus, Archive, Pencil, Trash2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import CommandCenterLayout from '@/frontend/layouts/CommandCenterLayout'
import AgentPicker, { type SelectableWorker } from '@/frontend/pages/CommandCenter/AgentChats/AgentPicker'
import { ConversationPanel } from '@/frontend/pages/Dashboard/AgentChats/components/ConversationPanel'
import { isAssistantUiEnabled } from '@/frontend/pages/Dashboard/AgentChats/runtime/assistantUiToggle'
import { validateAgentChatFiles } from '@/frontend/pages/Dashboard/AgentChats/attachments'
import { useAgentChatController } from '@/frontend/pages/Dashboard/AgentChats/useAgentChatController'
import type {
  AgentChatDigitalWorker,
  AgentChatProject,
  AgentChatSessionDetail,
  AgentChatSessionSummary,
} from '@/frontend/pages/Dashboard/AgentChats/types'
import type { SharedProps } from '@/frontend/types/inertia'
import { cn } from '@/lib/utils'

const AssistantConversationPanel = React.lazy(() =>
  import('@/frontend/features/AgentChats/assistant-ui/components/AssistantConversationPanel')
    .then((module) => ({ default: module.AssistantConversationPanel })),
)

interface Props {
  page_title: string
  chat_organization_id?: string | null
  chat_organization_name?: string | null
  chat_digital_worker?: AgentChatDigitalWorker | null
  chat_session?: AgentChatSessionDetail | null
  chat_sessions?: AgentChatSessionSummary[]
  chat_projects?: AgentChatProject[]
}

/**
 * Chat administrativo do Command Center (SZ-1742).
 *
 * A conversa em si é o MESMO componente do dashboard — mesmo runtime, mesmo
 * streaming, mesmas ações de turno. O que esta página acrescenta é a moldura de
 * operação: um seletor de agente que atravessa todas as organizações e um
 * rótulo permanente lembrando que a conversa é de equipe e roda na organização
 * do cliente.
 */
function CommandCenterAgentChats({
  page_title,
  chat_organization_id = null,
  chat_organization_name = null,
  chat_digital_worker = null,
  chat_session = null,
  chat_sessions = [],
  chat_projects = [],
}: Props) {
  const { auth } = usePage<SharedProps>().props
  const currentUserName = auth.user?.name || null
  const [useAssistantUi] = useState(isAssistantUiEnabled)
  const [draft, setDraft] = useState('')
  const [attachments, setAttachments] = useState<File[]>([])
  const recoveredDraftTokenRef = useRef<symbol | null>(null)

  // Trocar de agente atravessa organizações. Sem limpar o compositor, um
  // rascunho ou anexo escrito para um cliente sobrevive à navegação e pode ser
  // enviado para o agente de OUTRO cliente. O dashboard faz o mesmo ao trocar
  // de agente ou conversa.
  useEffect(() => {
    recoveredDraftTokenRef.current = null
    setDraft('')
    setAttachments([])
  }, [chat_digital_worker?.id, chat_session?.id])

  const updateUrlForSession = useCallback((sessionId: string | null) => {
    if (typeof window === 'undefined' || !chat_digital_worker) return

    const params = new URLSearchParams({ digital_worker_id: chat_digital_worker.id })
    if (sessionId) params.set('session_id', sessionId)
    else params.set('new_conversation', '1')

    if (!sessionId) router.cancel()
    window.history.pushState(window.history.state, '', `/cc/chat?${params.toString()}`)
  }, [chat_digital_worker])

  const chat = useAgentChatController({
    organizationId: chat_organization_id,
    digitalWorker: chat_digital_worker,
    initialSession: chat_session,
    initialSessions: chat_sessions,
    initialProjects: chat_projects,
    onSessionChange: updateUrlForSession,
    onReconnect: () => {
      // O agente e a organização entram no recarregamento parcial de propósito:
      // se o agente foi excluído enquanto a aba estava desconectada, sem eles o
      // Inertia preservaria o valor antigo e a página seguiria assinando e
      // enviando para um agente que não existe mais.
      router.reload({
        only: [
          'chat_organization_id',
          'chat_organization_name',
          'chat_digital_worker',
          'chat_session',
          'chat_sessions',
          'chat_projects',
        ],
      })
    },
  })

  const selectWorker = useCallback((worker: SelectableWorker) => {
    router.visit(`/cc/chat?digital_worker_id=${worker.id}`)
  }, [])

  const openSession = useCallback((sessionId: string) => {
    if (!chat_digital_worker) return
    router.visit(`/cc/chat?digital_worker_id=${chat_digital_worker.id}&session_id=${sessionId}`)
  }, [chat_digital_worker])

  const handleAssistantSubmit = useCallback(async (
    content: string,
    files: File[],
    onRecoveredCommit?: () => void,
    questionAnswer?: UserQuestionAnswer,
  ) => chat.submitMessage(content, files, undefined, onRecoveredCommit, questionAnswer), [chat.submitMessage])

  const handleAddAttachments = useCallback((incoming: File[]) => {
    const result = validateAgentChatFiles(attachments, incoming)
    if (result.error) {
      chat.setAttachmentError(result.error)
      return
    }
    setAttachments(result.files)
    chat.setAttachmentError(null)
  }, [attachments, chat.setAttachmentError])

  const handleSendMessage = async () => {
    const content = draft.trim()
    if (!content) return
    if (chat.submissionPhase !== 'idle') return

    const recoveryToken = Symbol()
    const files = attachments
    recoveredDraftTokenRef.current = null
    setDraft('')
    setAttachments([])
    // O compositor é limpo otimisticamente; se o envio falhar, o conteúdo volta.
    // Sem isso a mensagem e os anexos somem de vez numa rejeição do servidor.
    await chat.submitMessage(content, files, (pending) => {
      recoveredDraftTokenRef.current = recoveryToken
      setDraft(pending.draft)
      setAttachments(pending.attachments)
    }, () => {
      if (recoveredDraftTokenRef.current !== recoveryToken) return

      recoveredDraftTokenRef.current = null
      setDraft('')
      setAttachments([])
    })
  }

  const handleRename = useCallback((sessionId: string, currentTitle: string) => {
    const next = window.prompt('Renomear conversa', currentTitle)
    if (next === null) return
    void chat.renameSession(sessionId, next)
  }, [chat.renameSession])

  const handleCreateProject = useCallback(() => {
    const name = window.prompt('Nome do projeto')
    if (!name) return
    chat.createProject(name)
  }, [chat.createProject])

  const handleRenameProject = useCallback((projectId: string, currentName: string) => {
    const next = window.prompt('Renomear projeto', currentName)
    if (next === null) return
    chat.renameProject(projectId, next)
  }, [chat.renameProject])

  const handleDeleteProject = useCallback((projectId: string, name: string) => {
    if (!window.confirm(`Excluir o projeto "${name}"? As conversas dele não são apagadas.`)) return
    chat.deleteProject(projectId)
  }, [chat.deleteProject])

  const handleDelete = useCallback((sessionId: string, title: string) => {
    if (!window.confirm(`Excluir a conversa "${title}"? Esta ação não pode ser desfeita.`)) return
    void chat.deleteSession(sessionId)
  }, [chat.deleteSession])

  return (
    <>
      <Head title={page_title} />

      <div className="flex h-[calc(100dvh-6rem)] min-h-0 gap-4 p-4">
        <aside className="flex w-72 shrink-0 flex-col gap-4 overflow-hidden rounded-lg border border-border bg-card p-3">
          <AgentPicker activeWorkerId={chat_digital_worker?.id ?? null} onSelect={selectWorker} />

          {chat_digital_worker && (
            <div className="flex min-h-0 flex-col gap-1 border-t border-border pt-3">
              <div className="flex items-center justify-between gap-2">
                <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Conversas</span>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={chat.handleNewConversation}
                  data-testid="cc-chat-new-conversation"
                >
                  <Plus className="size-4" aria-hidden="true" />
                  Nova
                </Button>
              </div>
              {/* Projetos: o painel da conversa só ATRIBUI a projetos existentes,
                  então criar, renomear e excluir precisam de um caminho aqui —
                  sem isso as rotas `/cc/agent_chat_projects` ficariam inalcançáveis. */}
              <div className="flex items-center justify-between gap-2 pt-1">
                <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Projetos</span>
                <Button variant="ghost" size="sm" onClick={handleCreateProject} data-testid="cc-chat-new-project">
                  <Plus className="size-4" aria-hidden="true" />
                  Novo
                </Button>
              </div>
              {chat.projects.length > 0 && (
                <ul className="space-y-0.5">
                  {chat.projects.map((project) => (
                    <li key={project.id} className="group flex items-center gap-1">
                      <span className="min-w-0 flex-1 truncate px-2 py-1 text-sm text-muted-foreground">
                        {project.name}
                      </span>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="size-6 shrink-0 opacity-0 group-hover:opacity-100 focus-visible:opacity-100"
                            aria-label={`Ações do projeto ${project.name}`}
                            data-testid={`cc-chat-project-actions-${project.id}`}
                          >
                            <MoreHorizontal className="size-4" aria-hidden="true" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onSelect={() => handleRenameProject(project.id, project.name)}>
                            <Pencil className="size-4" aria-hidden="true" />
                            Renomear
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onSelect={() => handleDeleteProject(project.id, project.name)}
                            className="text-destructive focus:text-destructive"
                          >
                            <Trash2 className="size-4" aria-hidden="true" />
                            Excluir
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </li>
                  ))}
                </ul>
              )}

              <ul className="min-h-0 flex-1 space-y-0.5 overflow-y-auto">
                {chat.sessions.map((session) => (
                  <li key={session.id} className="group flex items-center gap-1">
                    <button
                      type="button"
                      onClick={() => openSession(session.id)}
                      data-testid={`cc-chat-session-${session.id}`}
                      className={cn(
                        'min-w-0 flex-1 truncate rounded-md px-2 py-1.5 text-left text-sm transition-colors hover:bg-accent',
                        chat.session?.id === session.id && 'bg-accent font-medium',
                      )}
                    >
                      {session.pinned && <Pin className="mr-1 inline size-3" aria-hidden="true" />}
                      {session.title}
                    </button>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="size-6 shrink-0 opacity-0 group-hover:opacity-100 focus-visible:opacity-100"
                          aria-label={`Ações da conversa ${session.title}`}
                          data-testid={`cc-chat-session-actions-${session.id}`}
                        >
                          <MoreHorizontal className="size-4" aria-hidden="true" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onSelect={() => handleRename(session.id, session.title)}>
                          <Pencil className="size-4" aria-hidden="true" />
                          Renomear
                        </DropdownMenuItem>
                        <DropdownMenuItem onSelect={() => { void chat.pinSession(session.id, !session.pinned) }}>
                          {session.pinned
                            ? <><PinOff className="size-4" aria-hidden="true" />Desafixar</>
                            : <><Pin className="size-4" aria-hidden="true" />Fixar</>}
                        </DropdownMenuItem>
                        <DropdownMenuItem onSelect={() => { void chat.archiveSession(session.id) }}>
                          <Archive className="size-4" aria-hidden="true" />
                          Arquivar
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onSelect={() => handleDelete(session.id, session.title)}
                          className="text-destructive focus:text-destructive"
                        >
                          <Trash2 className="size-4" aria-hidden="true" />
                          Excluir
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </li>
                ))}
              </ul>
              {chat.sessions.length === 0 && (
                <p className="px-2 py-3 text-sm text-muted-foreground">
                  Nenhuma conversa ainda. Busque um agente para começar.
                </p>
              )}
            </div>
          )}
        </aside>

        <section className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden rounded-lg border border-border bg-card">
          {chat_digital_worker ? (
            <>
              {/* Cabeçalho identifica o agente e a organização dona dele — é o
                  que diz em qual cliente esta conversa está rodando. */}
              <header className="flex items-center gap-2 border-b border-border px-4 py-2.5">
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium text-foreground">{chat_digital_worker.name}</p>
                  <p className="truncate text-xs text-muted-foreground">{chat_organization_name}</p>
                </div>
              </header>

              <div className="min-h-0 flex-1">
                {useAssistantUi ? (
                  <React.Suspense fallback={
                    <div className="flex h-full items-center justify-center text-sm text-muted-foreground">
                      Carregando conversa…
                    </div>
                  }>
                    <AssistantConversationPanel
                      error={chat.visibleError}
                      attachmentError={chat.attachmentError}
                      activity={chat.activity}
                      isRunning={chat.assistantIsRunning}
                      connectionStatus={chat.connectionStatus}
                      digitalWorker={chat_digital_worker}
                      session={chat.session}
                      currentUserName={currentUserName}
                      projects={chat.projects}
                      onAttachmentError={chat.setAttachmentError}
                      onStop={chat.abortCurrentTurn}
                      onRetry={chat.retryFailedTurn}
                      onFeedback={chat.submitMessageFeedback}
                      onBranch={chat.branchConversation}
                      onMoveSessionToProject={chat.moveSessionToProject}
                      onSubmit={handleAssistantSubmit}
                    />
                  </React.Suspense>
                ) : (
                  <ConversationPanel
                    fillContainer
                    draft={draft}
                    error={chat.visibleError}
                    attachmentError={chat.attachmentError}
                    attachments={attachments}
                    isSending={chat.submissionPhase !== 'idle'}
                    isUploadingAttachments={chat.submissionPhase === 'uploading'}
                    connectionStatus={chat.connectionStatus}
                    digitalWorker={chat_digital_worker}
                    session={chat.session}
                    currentUserName={currentUserName}
                    onDraftChange={setDraft}
                    onAddAttachments={handleAddAttachments}
                    onRemoveAttachment={(index) => setAttachments((files) => files.filter((_, i) => i !== index))}
                    onSendMessage={handleSendMessage}
                  />
                )}
              </div>
            </>
          ) : (
            <div
              className="flex h-full flex-col items-center justify-center gap-2 p-8 text-center"
              data-testid="cc-chat-no-worker"
            >
              <div className="flex size-14 items-center justify-center rounded-2xl bg-muted text-muted-foreground">
                <MessagesSquare className="size-7" aria-hidden="true" />
              </div>
              <h2 className="text-base font-semibold text-foreground">Escolha um agente</h2>
              <p className="max-w-sm text-sm text-muted-foreground">
                Busque por agente, instância ou organização para começar uma conversa de equipe.
              </p>
            </div>
          )}
        </section>
      </div>
    </>
  )
}

CommandCenterAgentChats.layout = (page: React.ReactElement) => <CommandCenterLayout>{page}</CommandCenterLayout>

export default CommandCenterAgentChats
